import { Component, inject, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { getLocaleFromUrl } from '../../../../shared/services/translate/locale-utils';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { GeolocationService } from '../../../../shared/services/geolocation/geolocation.service';
import { IdleTimeoutService } from '../../../../shared/services/idletimeout/idle-timeout.service';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { MonitoringService } from '../../../../shared/services/monitoring/monitoring-service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-pre-login-web',
  imports: [FormsModule, CommonModule],
  templateUrl: './pre-login.component.html',
  styleUrl: './pre-login.component.css'
})
export class PreLoginComponent implements OnDestroy {
  geolocation = inject(GeolocationService);
  mcAppGateway = inject(MCAppGatewayService);
  auth : AuthService = inject(AuthService);

  locale: string = '';
  currentLocation?: LocationCoordinates = undefined;
  isMobileResolution: boolean = false;
  public debugText = '';

  hospitalOptions: Array<any> = [];

  preloginPhoto: string = '';
  preloginHeadline: string = '';
  preloginFeatures: string[] = [];
  preloginDownloadAppHeader: string = '';
  preloginDownloadAppHeaderLine1: string = '';
  preloginDownloadAppHeaderLine2: string = '';
  preloginDownloadAppDetail: string = '';
  preloginDownloadAppAppStore: string = '';
  preloginDownloadAppGooglePlay: string = '';
  getCareNow: string = '';
  findAProvider: string = '';
  findALocation: string = '';

  // Image rotation properties - hardcoded for now, can be fetched from API later
  rotationImages: string[] = [
    'assets/images/rotation-img-1.svg'
  ];
  currentImageIndex: number = 0;
  currentRotationImage: string = this.rotationImages[0];
  private rotationInterval: any;

  // Login form properties
  username: string = '';
  password: string = '';
  showPassword: boolean = false;

  constructor(private translate: TranslateService, private idleTimeoutService: IdleTimeoutService, private monitoringService: MonitoringService) {
    this.locale = getLocaleFromUrl();
    sessionStorage.setItem("locale", this.locale);
    this.translate.use(this.locale);
 }

  async ngOnInit() {
    this.translate.get('PRELOGIN_HEADLINE').subscribe((translated: string) => {
      this.preloginHeadline = translated;
    });

    this.translate.get('PRELOGIN_FEATURES').subscribe((translated: string[]) => {
      this.preloginFeatures = translated;
    });

    this.translate.get('PRELOGIN_DOWNLOADAPP.HEADER').subscribe((translated: any) => {
      this.preloginDownloadAppHeader = translated;
    });

    this.translate.get('PRELOGIN_DOWNLOADAPP.HEADER_LINE1').subscribe((translated: any) => {
      this.preloginDownloadAppHeaderLine1 = translated;
    });

    this.translate.get('PRELOGIN_DOWNLOADAPP.HEADER_LINE2').subscribe((translated: any) => {
      this.preloginDownloadAppHeaderLine2 = translated;
    });

    this.translate.get('PRELOGIN_DOWNLOADAPP.DETAIL').subscribe((translated: any) => {
      this.preloginDownloadAppDetail = translated;
    });

    this.translate.get('PRELOGIN_DOWNLOADAPP.APPSTORE').subscribe((translated: string) => {
      this.preloginDownloadAppAppStore = translated;
    });

    this.translate.get('PRELOGIN_DOWNLOADAPP.GOOGLEPLAY').subscribe((translated: string) => {
      this.preloginDownloadAppGooglePlay = translated;
    });

    this.translate.get('GETCARENOW').subscribe((translated: string) => {
      this.getCareNow = translated;
    });

    this.translate.get('FINDAPROVIDER').subscribe((translated: string) => {
      this.findAProvider = translated;
    });

    this.translate.get('FINDALOCATION').subscribe((translated: string) => {
      this.findALocation = translated;
    });
        
    // Dynamically check if window resolution changes
    this.isMobileResolution = window.innerWidth <= 768;
    window.addEventListener('resize', () => {
      this.isMobileResolution = window.innerWidth <= 768;
      console.log(window.innerWidth);
      console.log(this.isMobileResolution ? "Mobile resolution" : "Desktop resolution");
    });

    // Add a timeout to the geolocation request
    const geolocationPromise = this.geolocation.getCurrentLocationAsync();
    const timeoutPromise = new Promise<LocationCoordinates>((_, reject) =>
      setTimeout(() => reject(new Error('Geolocation timeout')), 2000) // 2 seconds timeout
    );

    await Promise.race([geolocationPromise, timeoutPromise])
      .then(location => {
        this.currentLocation = location;
      })
      .catch(err => {
        console.error("Geolocation failed:", err);
      })
      .finally(() => {
        this.getData();
      });

    this.monitoringService.logEvent('Prelogin', { feature: 'pre-login', platform: 'web' });

    if (this.rotationImages.length > 1) {
      // Start image rotation
      this.startImageRotation();
    }
  }
  
  async allowLocation() {
    this.geolocation.getCurrentLocation().subscribe(location => {
      this.currentLocation = location;
      this.getData();
    });
  }

  getData() {
  }

  onGetCareNow() {  
    var deepLink = environment.web_get_care_now_deep_link_base_url;
    this.monitoringService.logEvent('Get Care Now', { feature: 'pre-login', platform: 'web' }); // log as web event
    
    console.log("Get Care Now clicked");
    console.log(deepLink);
      
    window.location.href = deepLink;
  }

  onProviderSearch() {  
    var deepLink = environment.web_provider_search_deep_link_base_url;
    this.monitoringService.logEvent('Provider Search', { feature: 'pre-login', platform: 'web' }); // log as web event
    
    console.log("Provider Search clicked");
    console.log(deepLink);
    
    window.open(deepLink, '_blank');
  }

  onLocationSearch() {  
    var deepLink = environment.web_location_search_deep_link_base_url;
    this.monitoringService.logEvent('Location Search', { feature: 'pre-login', platform: 'web' }); // log as web event

    console.log("Location Search clicked");
    console.log(deepLink);

    window.open(deepLink, '_blank');
  }

  // Image rotation methods
  startImageRotation() {
    // Rotate images every 3 seconds
    this.rotationInterval = setInterval(() => {
      this.rotateToNextImage();
    }, 3000);
  }

  rotateToNextImage() {
    this.currentImageIndex = (this.currentImageIndex + 1) % this.rotationImages.length;
    this.currentRotationImage = this.rotationImages[this.currentImageIndex];
  }

  setCurrentImage(index: number) {
    this.currentImageIndex = index;
    this.currentRotationImage = this.rotationImages[this.currentImageIndex];
  }

  stopImageRotation() {
    if (this.rotationInterval) {
      clearInterval(this.rotationInterval);
      this.rotationInterval = null;
    }
  }

  restartImageRotation() {
    this.stopImageRotation();
    this.startImageRotation();
  }

  ngOnDestroy() {
    this.stopImageRotation();
  }
}